import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Alert, Modal, Select, Space, Table, Tag, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { CATEGORIES, errorText } from './format';

const OUTCOME_COLORS = {
  created: 'green',
  paired: 'blue',
  amended: 'blue',
  duplicate: 'default',
  pending_entry: 'gold',
  error: 'red',
};

/**
 * Upload of notices and bank statements, any number and in any mix. Every file
 * comes back with what happened to it; unreadable ones are listed with the reason.
 */
export default function ImportModal({ open, company, projects, onClose, onImported }) {
  const { t } = useTranslation('taxReturns');
  const [files, setFiles] = useState([]);
  const [project, setProject] = useState(null);
  const [category, setCategory] = useState(null);
  const [result, setResult] = useState(null);

  const mutation = useMutation({
    mutationFn: () => API.taxReturns.importTaxDocuments(company, files, { project, category }),
    onSuccess: (data) => {
      setResult(data);
      setFiles([]);
      onImported?.();
    },
  });

  const close = () => {
    setFiles([]);
    setResult(null);
    mutation.reset();
    onClose();
  };

  const errors = result?.results?.filter((r) => r.outcome === 'error') || [];

  return (
    <Modal
      open={open}
      title={t('import.title')}
      width={820}
      onCancel={close}
      okText={result ? t('import.close') : t('import.submit', { count: files.length })}
      cancelButtonProps={{ shape: 'round', style: { display: result ? 'none' : undefined } }}
      okButtonProps={{ shape: 'round', disabled: !result && !files.length, loading: mutation.isPending }}
      onOk={result ? close : () => mutation.mutate()}
      destroyOnClose
    >
      {!result && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <p className="text-sm opacity-70">{t('import.help')}</p>
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder={t('import.projectPlaceholder')}
            value={project}
            onChange={setProject}
            options={projects.map((p) => ({ value: p._id, label: `${p.code} ${p.name}` }))}
          />
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder={t('import.categoryPlaceholder')}
            value={category}
            onChange={setCategory}
            options={CATEGORIES.map((c) => ({ value: c, label: t(`category.${c}`) }))}
          />
          <Upload.Dragger
            multiple
            accept=".pdf,.xml"
            fileList={files.map((f) => ({ uid: f.uid, name: f.name, status: 'done' }))}
            beforeUpload={(file) => {
              setFiles((list) => [...list, file]);
              return false;
            }}
            onRemove={(item) => setFiles((list) => list.filter((f) => f.uid !== item.uid))}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t('import.drop')}</p>
          </Upload.Dragger>
          {mutation.isError && <Alert type="error" showIcon message={errorText(t, mutation.error)} />}
        </Space>
      )}

      {result && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type={errors.length ? 'warning' : 'success'}
            showIcon
            message={t('import.done', {
              count: result.results.length,
              errors: errors.length,
            })}
          />
          <Table
            size="small"
            rowKey={(r, i) => `${r.fileName}-${i}`}
            dataSource={result.results}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            columns={[
              { title: t('import.file'), dataIndex: 'fileName', ellipsis: true },
              {
                title: t('import.kind'),
                dataIndex: 'kind',
                width: 110,
                render: (v) => (v ? t(`import.kinds.${v}`) : '—'),
              },
              {
                title: t('import.outcome'),
                dataIndex: 'outcome',
                width: 130,
                render: (v) => <Tag color={OUTCOME_COLORS[v]}>{t(`import.outcomes.${v}`)}</Tag>,
              },
              {
                title: t('import.message'),
                dataIndex: 'message',
                render: (v, r) =>
                  (r.code && `${t(`import.errors.${r.code}`)}${v ? ` (${v})` : ''}`) ||
                  (r.statements
                    ? t('import.statementsRead', {
                        count: r.statements.length,
                        lines: r.statements.reduce((s, x) => s + x.lines, 0),
                      })
                    : [r.title, r.bop].filter(Boolean).join(' · ')),
              },
            ]}
          />
        </Space>
      )}
    </Modal>
  );
}
